import { type RefObject, useEffect, useRef } from 'react';

export interface ClipboardFilesResult {
  files: File[];
}

interface UseClipboardFilesOptions {
  targetRef: RefObject<HTMLElement | null>;
  enabled?: boolean;
  onPasteFiles: (files: File[]) => void;
}

export const extractFilesFromClipboardData = (clipboardData: DataTransfer | null): ClipboardFilesResult => {
  if (!clipboardData) {
    return { files: [] };
  }

  const files: File[] = [];

  if (clipboardData.items && clipboardData.items.length > 0) {
    for (let i = 0; i < clipboardData.items.length; i += 1) {
      const item = clipboardData.items[i];
      if (item.kind !== 'file') continue;

      const file = item.getAsFile();
      if (file) {
        files.push(file);
      }
    }

    if (files.length > 0) {
      return { files };
    }
  }

  return { files: Array.from(clipboardData.files ?? []) };
};

export const useClipboardFiles = ({ targetRef, enabled = true, onPasteFiles }: UseClipboardFilesOptions) => {
  const onPasteFilesRef = useRef(onPasteFiles);
  onPasteFilesRef.current = onPasteFiles;

  useEffect(() => {
    const target = targetRef.current;
    if (!target || !enabled) {
      return;
    }

    const handlePaste = (event: ClipboardEvent) => {
      const { files } = extractFilesFromClipboardData(event.clipboardData);
      if (files.length === 0) {
        return;
      }

      event.preventDefault();
      onPasteFilesRef.current(files);
    };

    target.addEventListener('paste', handlePaste);

    return () => {
      target.removeEventListener('paste', handlePaste);
    };
  }, [enabled, targetRef]);
};
